import { PageIndices, TabIndices, SectionIndices } from '../../common/i-doc/i-doc.registry.ts';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Rating,
  Typography,
} from '@mui/material';
import { getMyReviews, ExpertReview } from '../../services/expert.service';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { AppointmentSettings } from '@components/settings/AppointmentSettings.tsx';
import { ScheduleSettings } from '@components/settings/ScheduleSettings.tsx';
import { TimeOffSettings } from '@components/settings/TimeOffSettings.tsx';
import { useSearchParams } from 'react-router-dom';
import { AdaptiveTabs, AdaptiveTabItem } from '@components/common/AdaptiveTabs';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint';
import { UniversalPagination } from '@components/common/UniversalPagination';
import { toastService } from '../../services/toast.service';
import { ExpertAppointmentsList } from '../../components/appointments/expert/ExpertAppointmentsList';

const APPOINTMENTS_TABS: AdaptiveTabItem[] = [
  { value: 'appointments', label: 'Записи на зустріч' },
  { value: 'reviews',      label: 'Відгуки' },
  { value: 'settings',     label: 'Налаштування' },
];

const ExpertAppointmentsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') ?? 'appointments';

  const [reviews, setReviews] = useState<ExpertReview[]>([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsLimit, setReviewsLimit] = useState(10);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const loadReviews = useCallback(async (page: number, limit: number) => {
    setReviewsLoading(true);
    try {
      const res = await getMyReviews({ page, limit });
      setReviews(res.data);
      setReviewsTotal(res.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити відгуки.');
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentTab === 'reviews') {
      loadReviews(reviewsPage, reviewsLimit);
    }
  }, [currentTab, reviewsPage, reviewsLimit, loadReviews]);

  return (
    <DashboardLayout data-i-doc={PageIndices.EXPERT_APPOINTMENTS}>
      <Box>
        <PageHint iDoc={PageIndices.EXPERT_APPOINTMENTS} />

        <AdaptiveTabs tabs={APPOINTMENTS_TABS} searchParamKey="tab" defaultValue="appointments" />

        {currentTab === 'appointments' && (
          <Box sx={{ pt: 3 }} data-i-doc={TabIndices.EXPERT_APPOINTMENTS_LIST}>
            <PageHint iDoc={TabIndices.EXPERT_APPOINTMENTS_LIST} />
            <Box data-i-doc={SectionIndices.EXPERT_APPOINTMENTS_LIST}>
              <PageHint iDoc={SectionIndices.EXPERT_APPOINTMENTS_LIST} />
              <ExpertAppointmentsList />
            </Box>
          </Box>
        )}

        {currentTab === 'reviews' && (
          <Box sx={{ pt: 3 }} data-i-doc={TabIndices.EXPERT_APPOINTMENTS_REVIEWS}>
            <PageHint iDoc={TabIndices.EXPERT_APPOINTMENTS_REVIEWS} />
            <PageCard>
              {reviewsLoading ? (
                <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
                  <CircularProgress />
                </Box>
              ) : reviews.length === 0 ? (
                <Typography sx={{ mt: 2, textAlign: 'center' }}>Відгуків ще немає.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {reviews.map(review => (
                    <Card key={review.id} variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                          <Avatar src={review.client.avatar ?? undefined} sx={{ width: 36, height: 36 }}>
                            {review.client.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">{review.client.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(review.createdAt).toLocaleDateString('uk-UA')}
                            </Typography>
                          </Box>
                          <Box sx={{ ml: 'auto' }}>
                            <Rating value={review.rating} readOnly size="small" />
                          </Box>
                        </Box>
                        {review.comment && (
                          <Typography variant="body2" color="text.secondary">{review.comment}</Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
              <UniversalPagination
                page={reviewsPage}
                count={Math.ceil(reviewsTotal / reviewsLimit)}
                limit={reviewsLimit}
                total={reviewsTotal}
                onPageChange={(p) => setReviewsPage(p)}
                onLimitChange={(v) => { setReviewsLimit(v); setReviewsPage(1); }}
                storageKey="pagination:expert-reviews"
              />
            </PageCard>
          </Box>
        )}

        {currentTab === 'settings' && (
          <Box sx={{ pt: 3 }} data-i-doc={TabIndices.EXPERT_APPOINTMENTS_SETTINGS}>
            <PageHint iDoc={TabIndices.EXPERT_APPOINTMENTS_SETTINGS} />
            <AppointmentSettings />
            <ScheduleSettings />
            <TimeOffSettings />
          </Box>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ExpertAppointmentsPage;
